import React, { useState, useRef, useEffect } from "react";

const highlightMatch = (label, search) => {
  if (!search) return label;

  const regex = new RegExp(`(${search})`, "ig");
  return label.replace(regex, "<mark>$1</mark>");
};

const SearchableSelect = ({ options = [], value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);

  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  // When selected value changes and dropdown is closed → show label
  useEffect(() => {
    if (!open) {
      const selected = options.find((o) => o.value === value);
      setInputValue(selected ? selected.label : "");
    }
  }, [value, options, open]);

  // FILTER LOGIC
  const filtered = options.filter((opt) => {
    const searchVal = inputValue.trim().toLowerCase();

    // 1️⃣ If empty → show all
    if (!searchVal) return true;

    const [code = "", name = ""] = opt.label.toLowerCase().split(" - ");

    // 2️⃣ If numeric → match code startsWith
    if (!isNaN(searchVal)) {
      return code.startsWith(searchVal);
    }

    // 3️⃣ Otherwise → match name includes
    return name.includes(searchVal);
  });

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && open && filtered.length > 0) {
      const item = listRef.current.children[highlightIndex];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex, open, filtered]);

  // Close dropdown outside click
  useEffect(() => {
    const listener = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, []);

  const handleSelect = (val) => {
    const selected = options.find((o) => o.value === val);
    onChange(val);
    setInputValue(selected?.label || "");
    setOpen(false);
    setHighlightIndex(0);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev === 0 ? filtered.length - 1 : prev - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const selected = filtered[highlightIndex];
      if (selected) handleSelect(selected.value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="position-relative w-100" ref={wrapperRef}>
      <input
        className="form-control"
        placeholder={placeholder || "Search..."}
        value={inputValue}
        onFocus={() => {
          setOpen(true);
          setHighlightIndex(0);
        }}
        onChange={(e) => {
          const val = e.target.value;

          setInputValue(val);
          setHighlightIndex(0);

          // 🔥 If user clears input → also clear selected value
          if (val === "") {
            onChange(null);
            setOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
      />

      {open && (
        <div
          className="position-absolute bg-white border rounded shadow-sm mt-1"
          style={{
            width: "100%",
            maxHeight: "250px",
            overflowY: "auto",
            zIndex: 9999,
          }}
        >
          <ul
            className="list-group list-group-flush"
            ref={listRef}
            style={{ marginBottom: 0 }}
          >
            {filtered.length === 0 && (
              <li className="list-group-item text-muted">
                No results
              </li>
            )}

            {filtered.map((opt, idx) => (
              <li
                key={opt.value}
                className={`list-group-item list-group-item-action ${
                  idx === highlightIndex ? "active" : ""
                }`}
                style={{ cursor: "pointer" }}
                onMouseDown={() => handleSelect(opt.value)}
                dangerouslySetInnerHTML={{
                  __html: highlightMatch(opt.label, inputValue),
                }}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;